import { useBusinessFunctionService } from 'domain/shared/businessFunction';
import { isTechnologyRequired, useTechnologyService } from 'domain/shared/career';
import { useOfficeService, usePositionService } from 'domain/shared/employee';
import { enrichPositionSlots } from 'domain/shared/positionSlot';
import { useCrudService } from 'framework/crud';
import type { ApiRequestOptions } from 'framework/api';
import type { ListResponse, LoadingParams } from 'framework/list';
import { usePeopleManagementApi } from 'infrastructure/api';
import { POSITION_SLOT_LIFECYCLE, POSITION_SLOT_OCCUPANCY } from './constants';
import type {
  PositionSlotAssignmentFormValues,
  PositionSlotFormValues,
  PreviewPositionSlot,
  PreviewPositionSlotListItem,
} from './types';

const RESOURCE_URL = '/position-slots';

const normalizePositionSlot = (positionSlot: PreviewPositionSlot): PreviewPositionSlot => {
  const employeeAliases =
    positionSlot.employeeAliases ?? (positionSlot.employeeAlias ? [positionSlot.employeeAlias] : []);
  return {
    ...positionSlot,
    employeeAliases,
    employeeAlias: positionSlot.employeeAlias ?? employeeAliases[0] ?? null,
    lifecycle: positionSlot.lifecycle ?? POSITION_SLOT_LIFECYCLE.ACTIVE,
    occupancy:
      positionSlot.occupancy ??
      (employeeAliases.length || positionSlot.positionFulfillmentRequestId
        ? POSITION_SLOT_OCCUPANCY.OCCUPIED
        : POSITION_SLOT_OCCUPANCY.VACANT),
    positionFulfillmentRequestId: positionSlot.positionFulfillmentRequestId ?? null,
    requiredReplacement: positionSlot.requiredReplacement ?? false,
    requiresConfirmation: positionSlot.requiresConfirmation ?? false,
  };
};

const usePositionSlotsService = () => {
  const { sendPatchRequest } = usePeopleManagementApi();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllOffices } = useOfficeService();
  const { findAll: findAllBusinessFunctions } = useBusinessFunctionService();
  const { search, create, update, getById } = useCrudService<PreviewPositionSlot, ListResponse<PreviewPositionSlot>>({
    listResourceUrl: RESOURCE_URL,
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    useService: usePeopleManagementApi,
  });

  const enrichPositionSlotList = async (
    positionSlots: PreviewPositionSlot[],
  ): Promise<PreviewPositionSlotListItem[]> => {
    const [positions, technologies, offices, businessFunctions] = await Promise.all([
      findAllPositions(),
      findAllTechnologies(),
      findAllOffices(),
      findAllBusinessFunctions(),
    ]);

    return enrichPositionSlots(positionSlots.map(normalizePositionSlot), {
      positions,
      technologies,
      offices,
      businessFunctions,
    }) as PreviewPositionSlotListItem[];
  };

  // Omits `secondaryCompetencies`: the backend keeps the stored list when the field is absent.
  const buildPayload = (data: PositionSlotFormValues | PreviewPositionSlot) => {
    const specializationId = Number(data.specializationId);

    return {
      specializationId,
      technologyId: isTechnologyRequired(specializationId) ? data.technologyId || null : null,
      competencyLevel: data.competencyLevel,
      officeIds: data.officeIds.map(Number),
      workerType: data.workerType,
      workRateType: data.workRateType,
      businessFunctionId: data.businessFunctionId,
      positionProfileId: data.positionProfileId ?? null,
    };
  };

  // Always sends the list: the backend replaces the stored one (`[]` clears it).
  const buildFormPayload = (data: PositionSlotFormValues) => ({
    ...buildPayload(data),
    secondaryCompetencies: (data.secondaryCompetencies ?? []).map(
      ({ specializationId, technologyId, competencyLevel }) => ({
        specializationId: Number(specializationId),
        technologyId: isTechnologyRequired(specializationId) ? technologyId || null : null,
        competencyLevel,
      }),
    ),
  });

  const getPositionSlotsPage = async (loadParams: LoadingParams, options?: ApiRequestOptions) => {
    const response = await search(loadParams, options);
    options?.signal?.throwIfAborted();
    const positionSlots = await enrichPositionSlotList(response.result || []);

    return {
      result: positionSlots,
      pageNumber: response.pageNumber,
      totalPages: response.totalPages,
      totalElements: response.totalElements ?? positionSlots.length,
    };
  };

  const getPositionSlot = async (positionSlotId: string): Promise<PreviewPositionSlotListItem | null> => {
    const positionSlot = await getById(positionSlotId, true);
    const [enrichedPositionSlot] = await enrichPositionSlotList([positionSlot]).catch(cause => {
      throw new Error('Could not load position details.', { cause });
    });

    return enrichedPositionSlot;
  };

  const patchPositionSlot = async (positionSlotId: string, action: string, body?: Record<string, unknown>) => {
    const response = await sendPatchRequest(`${RESOURCE_URL}/${positionSlotId}/${action}`, body);
    return normalizePositionSlot((await response.json()) as PreviewPositionSlot);
  };

  const assignEmployee = (positionSlotId: string, employeeAlias: string) =>
    patchPositionSlot(positionSlotId, 'assign', { employeeAlias });

  const unassignEmployee = (positionSlotId: string, employeeAlias: string) =>
    patchPositionSlot(positionSlotId, 'unassign', { employeeAlias });

  const createPositionSlot = (data: PositionSlotFormValues) => create(buildFormPayload(data));

  const updatePositionSlot = (positionSlotId: string, data: PositionSlotFormValues) =>
    update(positionSlotId, buildFormPayload(data));

  const updatePositionSlotAssignment = async (
    positionSlot: PreviewPositionSlot,
    data: PositionSlotAssignmentFormValues,
  ) => {
    const currentEmployeeAlias = positionSlot.employeeAlias ?? positionSlot.employeeAliases?.[0] ?? null;
    const nextEmployeeAlias = data.employeeAlias || null;
    const isEmployeeChanged = currentEmployeeAlias !== nextEmployeeAlias;

    if (!isEmployeeChanged) {
      return update(positionSlot.id, {
        ...buildPayload(positionSlot),
        requiredReplacement: Boolean(nextEmployeeAlias && data.requiredReplacement),
      });
    }

    let result = positionSlot;

    if (currentEmployeeAlias) {
      result = await unassignEmployee(positionSlot.id, currentEmployeeAlias);
    }

    if (nextEmployeeAlias) {
      result = await assignEmployee(positionSlot.id, nextEmployeeAlias);
    }

    if (nextEmployeeAlias && data.requiredReplacement) {
      result = await update(positionSlot.id, {
        ...buildPayload(positionSlot),
        requiredReplacement: data.requiredReplacement,
      });
    }

    return result;
  };

  return {
    getPositionSlotsPage,
    getPositionSlot,
    createPositionSlot,
    updatePositionSlot,
    updatePositionSlotAssignment,
    freezePositionSlot: (positionSlotId: string) => patchPositionSlot(positionSlotId, 'freeze'),
    unfreezePositionSlot: (positionSlotId: string) => patchPositionSlot(positionSlotId, 'unfreeze'),
    retirePositionSlot: (positionSlotId: string) => patchPositionSlot(positionSlotId, 'retire'),
  };
};

export default usePositionSlotsService;
